import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { createDesig, getDesig, listDepts, updateDesig, type DeptRow } from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';

export function DesignationFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { desigId } = useParams<{ desigId: string }>();
  const editing = Boolean(desigId);

  const [desigCode, setDesigCode] = useState('');
  const [desigName, setDesigName] = useState('');
  const [deptId, setDeptId] = useState('');
  const [grade, setGrade] = useState('');
  const [desigLevel, setDesigLevel] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [depts, setDepts] = useState<DeptRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listDepts(token).then((r) => setDepts(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!desigId) return;
    getDesig(token, desigId)
      .then((d) => {
        setDesigCode(d.desigCode);
        setDesigName(d.desigName);
        setDeptId(d.deptId ?? '');
        setGrade(d.grade ?? '');
        setDesigLevel(d.desigLevel != null ? String(d.desigLevel) : d.level != null ? String(d.level) : '');
        setEffectiveFromDate(d.effectiveFromDate ?? todayIso());
      })
      .catch((e) => setError((e as Error).message));
  }, [token, desigId]);

  async function submit(): Promise<void> {
    if (!desigName.trim()) { setError(t('org.designation.form.nameReq')); return; }
    if (!editing) {
      if (!desigCode.trim()) { setError(t('org.designation.form.codeReq')); return; }
      if (!deptId) { setError(t('org.designation.form.deptReq')); return; }
    }
    setError(null);
    setBusy(true);
    try {
      if (editing && desigId) {
        await updateDesig(token, desigId, {
          desigName: desigName.trim(),
          grade: grade.trim() || undefined,
          desigLevel: desigLevel ? Number(desigLevel) : undefined,
        });
      } else {
        await createDesig(token, {
          desigCode: desigCode.trim(),
          desigName: desigName.trim(),
          deptId,
          grade: grade.trim() || undefined,
          desigLevel: desigLevel ? Number(desigLevel) : undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/designations');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const deptOptions = depts.map((d) => ({ value: d.deptId, label: `${d.deptCode} — ${d.deptName}` }));

  return (
    <FormShell label="designation-form" title={editing ? t('org.designation.form.editTitle') : t('org.designation.form.newTitle')} error={error} onSubmit={submit}>
      <Field label={t('org.field.code')} name="desigCode" value={desigCode} onChange={setDesigCode} required placeholder={t('org.designation.form.codePh')} />
      <Field label={t('org.field.name')} name="desigName" value={desigName} onChange={setDesigName} required />
      <SelectField label={t('org.field.department')} name="deptId" value={deptId} onChange={setDeptId} options={deptOptions} required />
      <Field label={t('org.field.grade')} name="grade" value={grade} onChange={setGrade} />
      <Field label={t('org.field.level')} name="desigLevel" value={desigLevel} onChange={setDesigLevel} type="number" />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.designation.form.create')} cancelTo="/org/designations" />
    </FormShell>
  );
}
